import {
  outcomesFor,
  type AttentionKind,
  type Outcome,
  type TaskChoice,
} from '@/features/tasks';
import { reworkOf, type QueueRow } from './yourTurn';

/**
 * What a Your turn row can do, as choices to hold (spec 154): its first
 * action for the A key, its rework for R, and the words a held action wears.
 * Read at call time only (the tasks package sits on an import cycle with this
 * one, spec 152).
 *
 * A reader (`canWork` false, from `useCanWork()`) has no choices: the service
 * would refuse every one, so neither key offers it (spec 193). What a reader
 * may do with a row (Open run, What it produced) is a link, not a choice.
 */

/** Kinds a person can run again. A spend limit is not the member's to lift. */
export const RERUN_KINDS: ReadonlySet<AttentionKind> = new Set<AttentionKind>([
  'failed-run',
  'stopped',
]);

/** The words for a held action on its row. */
export function choiceLabel(choice: TaskChoice, row: QueueRow): string {
  switch (choice.kind) {
    case 'decide':
      return choice.outcome.label;
    case 'run-again':
      return 'Run again';
    case 'start-subtasks':
      return `Start ${String(row.toStart ?? 0)}`;
  }
}

function decide(row: QueueRow, outcome: Outcome): TaskChoice {
  const targetLabel =
    row.steps.find((step) => step.stepId === outcome.to)?.label ?? outcome.to;
  return { kind: 'decide', outcome, targetLabel };
}

/** The row's first action (A): what its leading button does. */
export function firstChoice(row: QueueRow, canWork = true): TaskChoice | null {
  if (!row.hasBoard || !canWork) return null;
  switch (row.severity) {
    case 'decision': {
      const [first] = outcomesFor(row.step, row.steps);
      return first ? decide(row, first) : null;
    }
    case 'to-start':
      return (row.toStart ?? 0) > 0 ? { kind: 'start-subtasks' } : null;
    case 'wrong':
      return RERUN_KINDS.has(row.kind) ? { kind: 'run-again' } : null;
  }
}

/** The row's rework (R): the one choice leading back, on a decision. */
export function reworkChoice(row: QueueRow, canWork = true): TaskChoice | null {
  if (row.severity !== 'decision' || !row.hasBoard || !canWork) return null;
  const outcome = reworkOf(row);
  return outcome ? decide(row, outcome) : null;
}
